/**
 * ==========================================================================
 *  GEMEINSAME AUFGABEN — Testansicht
 * ==========================================================================
 *  Arbeitet BEWUSST auf denselben Originalaufgaben wie das Cockpit:
 *  gleiche Tabelle, gleiche Endpunkte (/api/todos), gleiche IDs. Keine
 *  zweite Aufgabenliste, kein Abgleichdienst.
 *
 *  Aufbau der Liste (Hendriks Vorgabe vom 21.10.):
 *   * Eine Zeile je Aufgabe — Projekt-Kuerzel, Text, Frist, Gewichtung.
 *     Klick auf die Zeile klappt die Bearbeitung auf. So bleibt die Liste
 *     auch bei dreissig Aufgaben noch lesbar.
 *   * IMMER nach Gewichtung sortiert (A vor B vor C), mit Ueberschriften —
 *     auch wenn nach einem einzelnen Projekt gefiltert wird.
 *   * Projekte farbig, mit festem Kuerzel.
 *
 *  Unveraendert gilt:
 *   * Frist und Wiedervorlage sind getrennt; das Verschieben der einen
 *     laesst die andere in Ruhe.
 *   * 'Erledigt' wird ausschliesslich ueber den Haken gesetzt.
 *   * Jeder Schreibvorgang schickt den gesehenen Stand mit; bei einer
 *     Fremdaenderung kommt 409 statt stillem Ueberschreiben.
 *   * Kein vorgetaeuschter Erfolg: die Anzeige aendert sich erst, wenn
 *     der Server bestaetigt hat.
 * ==========================================================================
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { api, ApiError } from '../api';

/* ----------------------------------------------------------------------
 *  Die Projekte. EINZIGE Stelle, an der Name, Kuerzel und Farbe stehen —
 *  ein neues Projekt ist hier eine Zeile. Der in der Datenbank
 *  gespeicherte Wert ist der Name; Kuerzel und Farbe sind reine Anzeige.
 *  Ein Projekt, das hier nicht steht, funktioniert trotzdem (grau).
 * -------------------------------------------------------------------- */
const PROJEKTE = [
  // SPA hat Unterbereiche. Gespeichert wird der volle Name ("SPA: Touren"),
  // damit in der Datenbank sofort erkennbar ist, wohin er gehoert, und
  // damit ein spaeteres "Touren" aus einem anderen Projekt nicht kollidiert.
  // Angezeigt wird nur der kurze Teil.
  { name: 'SPA',           kuerzel: 'SPA', chip: 'bg-purple-100 text-purple-900 border-purple-300',    balken: 'bg-purple-500',
    unter: [
      { name: 'SPA: Protokolle',   label: 'Protokolle',   kuerzel: 'PRO', quelle: 'protokolle' },
      { name: 'SPA: Neue Termine', label: 'Neue Termine', kuerzel: 'TER', quelle: 'neue_termine' },
      { name: 'SPA: Touren',       label: 'Touren',       kuerzel: 'TOU', quelle: 'touren' },
    ] },
  { name: 'TEUS',          kuerzel: 'TEU', chip: 'bg-orange-100 text-orange-900 border-orange-300',    balken: 'bg-orange-500' },
  { name: 'Leads',         kuerzel: 'LEA', chip: 'bg-emerald-100 text-emerald-900 border-emerald-300', balken: 'bg-emerald-500' },
  { name: 'Kurse',         kuerzel: 'KUR', chip: 'bg-sky-100 text-sky-900 border-sky-300',             balken: 'bg-sky-500' },
  { name: 'Finanzen',      kuerzel: 'FIN', chip: 'bg-amber-100 text-amber-900 border-amber-300',       balken: 'bg-amber-500' },
  { name: 'Steuer',        kuerzel: 'STE', chip: 'bg-rose-100 text-rose-900 border-rose-300',          balken: 'bg-rose-500' },
  { name: 'Privates iTKI', kuerzel: 'ITK', chip: 'bg-teal-100 text-teal-900 border-teal-300',          balken: 'bg-teal-500' },
];

const PROJEKT_LOOKUP = new Map(PROJEKTE.map((p) => [p.name.toLowerCase(), p]));

// Unterbereiche erben Farbe und Balken vom Oberprojekt, haben aber ein
// eigenes Kuerzel und einen kurzen Anzeigenamen.
PROJEKTE.forEach((p) => (p.unter || []).forEach((u) => {
  PROJEKT_LOOKUP.set(u.name.toLowerCase(), {
    name: u.label, kuerzel: u.kuerzel, chip: p.chip, balken: p.balken, oberprojekt: p.name,
  });
}));

// Gehoert eine Aufgabe zu einem Projekt? Ein Unterbereich zaehlt immer
// mit — wer "SPA" filtert, will die Touren und Protokolle mit sehen.
const gehoertZu = (aufgabe, projekt) =>
  (aufgabe.kategorien || []).some((k) =>
    k.toLowerCase() === projekt.toLowerCase() ||
    k.toLowerCase().startsWith(`${projekt.toLowerCase()}: `));

// Fuer ein nicht hinterlegtes Projekt: neutrales Grau, Kuerzel aus den
// ersten drei Buchstaben. Damit geht nichts kaputt, wenn jemand etwas
// Eigenes eintippt.
const projektInfo = (name) =>
  PROJEKT_LOOKUP.get((name || '').toLowerCase()) || {
    name,
    kuerzel: (name || '?').slice(0, 3).toUpperCase(),
    chip: 'bg-gray-100 text-gray-800 border-gray-300',
    balken: 'bg-gray-400',
  };

const PRIOS = [
  { code: 'A', erklaerung: 'dringend',    an: 'bg-red-600 text-white border-red-600',     aus: 'text-red-700 border-red-300 hover:bg-red-50',       kopf: 'text-red-700' },
  { code: 'B', erklaerung: 'normal',      an: 'bg-amber-500 text-white border-amber-500', aus: 'text-amber-700 border-amber-300 hover:bg-amber-50', kopf: 'text-amber-700' },
  { code: 'C', erklaerung: 'kann warten', an: 'bg-sky-600 text-white border-sky-600',     aus: 'text-sky-700 border-sky-300 hover:bg-sky-50',       kopf: 'text-sky-700' },
];
const PRIO_RANG = { A: 0, B: 1, C: 2 };
const OHNE_PROJEKT = '__ohne__';

/* ---------------------------- Datums-Helfer --------------------------- */
const heuteISO = () => new Date().toISOString().slice(0, 10);

const plusTage = (basisISO, tage) => {
  const d = basisISO ? new Date(`${basisISO}T12:00:00`) : new Date();
  d.setDate(d.getDate() + tage);
  return d.toISOString().slice(0, 10);
};

const deutsch = (iso) => {
  if (!iso) return null;
  const [j, m, t] = iso.split('-');
  return `${t}.${m}.${j}`;
};

const tageBis = (iso) =>
  iso ? Math.round((new Date(`${iso}T12:00:00`) - new Date(`${heuteISO()}T12:00:00`)) / 86400000) : null;

const lageText = (iso) => {
  const d = tageBis(iso);
  if (d === null) return null;
  if (d < 0) return `${Math.abs(d)} Tage überfällig`;
  if (d === 0) return 'heute';
  if (d === 1) return 'morgen';
  return `in ${d} Tagen`;
};

/* ------------------------- kleine Bausteine --------------------------- */
function DatumsFeld({ titel, hinweis, wert, onAendern, farbe }) {
  const lage = lageText(wert);
  return (
    <div className="flex-1 min-w-[150px]">
      <div className="flex items-baseline justify-between gap-2">
        <span className={`text-sm font-semibold ${farbe}`}>{titel}</span>
        {lage && <span className="text-xs text-gray-500">{lage}</span>}
      </div>
      <input type="date" value={wert || ''} onChange={(e) => onAendern(e.target.value || null)}
             className="mt-1 w-full rounded-lg border border-gray-300 px-2 text-base"
             style={{ minHeight: 44 }} />
      <p className="mt-0.5 text-xs text-gray-500">{hinweis}</p>
    </div>
  );
}

// Eigener Zustand, damit das Tippen nicht die ganze Liste neu aufbaut und
// das Feld dabei den Fokus verliert.
function ProjektHinzufuegen({ onHinzufuegen }) {
  const [wert, setWert] = useState('');
  const uebernehmen = () => {
    const t = wert.trim();
    if (!t) return;
    onHinzufuegen(t);
    setWert('');
  };
  return (
    <input
      list="pd-projekte" value={wert}
      onChange={(e) => setWert(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); uebernehmen(); } }}
      onBlur={uebernehmen}
      onClick={(e) => e.stopPropagation()}
      placeholder="+ Projekt"
      className="w-32 rounded-full border border-dashed border-gray-400 px-3 text-sm"
      style={{ minHeight: 36 }}
    />
  );
}

function AnmeldungNoetig({ onFertig }) {
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [meldung, setMeldung] = useState(null);
  const [laeuft, setLaeuft] = useState(false);

  const anmelden = async (e) => {
    e.preventDefault();
    setLaeuft(true); setMeldung(null);
    try {
      await api.post('/api/auth/login', { email: email.trim(), passwort });
      await onFertig();
    } catch (err) {
      console.warn('Anmeldung fehlgeschlagen', err);
      setMeldung(err instanceof ApiError && err.status === 401
        ? 'E-Mail oder Passwort stimmt nicht.'
        : `Anmeldung nicht möglich: ${err.message}`);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-3 py-10">
      <form onSubmit={anmelden} className="mx-auto max-w-sm rounded-xl border border-gray-200 bg-white p-5">
        <h1 className="text-xl font-bold text-gray-900">Gemeinsame Aufgaben</h1>
        <p className="mb-4 mt-1 text-sm text-gray-600">Bitte einmal anmelden.</p>
        <label className="block text-sm font-semibold text-gray-700">E-Mail</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
               autoComplete="username" required
               className="mt-1 w-full rounded-lg border border-gray-300 px-3 text-base" style={{ minHeight: 44 }} />
        <label className="mt-3 block text-sm font-semibold text-gray-700">Passwort</label>
        <input type="password" value={passwort} onChange={(e) => setPasswort(e.target.value)}
               autoComplete="current-password" required
               className="mt-1 w-full rounded-lg border border-gray-300 px-3 text-base" style={{ minHeight: 44 }} />
        {meldung && <p className="mt-3 text-sm text-red-700">{meldung}</p>}
        <button type="submit" disabled={laeuft}
                className="mt-4 w-full rounded-lg bg-gray-900 text-base font-semibold text-white disabled:opacity-40"
                style={{ minHeight: 44 }}>
          {laeuft ? 'einen Moment…' : 'Anmelden'}
        </button>
      </form>
    </div>
  );
}

/* ============================ Hauptansicht ============================ */
export default function Aufgaben() {
  const [aufgaben, setAufgaben] = useState([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState(null);
  const [konflikt, setKonflikt] = useState(null);
  const [speichert, setSpeichert] = useState(null);
  const [angemeldet, setAngemeldet] = useState(true);
  const [offeneId, setOffeneId] = useState(null);   // aufgeklappte Aufgabe

  const [neuText, setNeuText] = useState('');
  const [neuFrist, setNeuFrist] = useState('');
  const [neuProjekt, setNeuProjekt] = useState('');

  const [filterProjekt, setFilterProjekt] = useState(null);
  const [filterUnter, setFilterUnter] = useState(null);   // nur innerhalb eines Projekts
  const [filterPrio, setFilterPrio] = useState(null);
  const [zeigeErledigte, setZeigeErledigte] = useState(false);
  // Welche Klappleiste oben offen ist. null = keine — dann steht die
  // Liste direkt unter der Leiste, was auf dem Handy den Unterschied macht.
  const [offenesPanel, setOffenesPanel] = useState(null);  // 'neu' | 'projekt' | 'prio'
  const [suche, setSuche] = useState('');
  const [sucheOffen, setSucheOffen] = useState(false);   // nur auf schmalen Schirmen
  // Die drei SPA-Listen, die das Cockpit selbst kennt. Werden bei jedem
  // Laden neu geholt und nie gespeichert — sie koennen damit nicht
  // veralten und muessen nicht aufgeraeumt werden.
  const [hinweise, setHinweise] = useState({ protokolle: [], neue_termine: [], touren: [] });
  // Termine, bei denen noch offen ist, ob sie stattgefunden haben.
  const [nachfragen, setNachfragen] = useState([]);
  const [nachfrageLaeuft, setNachfrageLaeuft] = useState(null);
  const [nachfrageEcho, setNachfrageEcho] = useState(null);  // kurze Rueckmeldung

  const neuLaden = useCallback(async () => {
    setLaden(true);
    try {
      const liste = await api.get('/api/todos');
      setAufgaben(Array.isArray(liste) ? liste : []);
      setAngemeldet(true);
      setFehler(null);
      // Die Cockpit-Listen sind eine Zugabe — wenn sie nicht kommen,
      // soll die Aufgabenliste trotzdem stehen. Deshalb eigener Versuch
      // mit eigenem Fangen statt im selben try.
      try {
        const h = await api.get('/api/aufgaben/spa-hinweise');
        setHinweise({
          protokolle: h?.protokolle || [],
          neue_termine: h?.neue_termine || [],
          touren: h?.touren || [],
        });
      } catch (e2) {
        console.warn('Cockpit-Listen konnten nicht geladen werden', e2);
      }
      try {
        const n = await api.get('/api/aufgaben/termin-nachfragen');
        setNachfragen(n?.nachfragen || []);
      } catch (e3) {
        console.warn('Termin-Nachfragen konnten nicht geladen werden', e3);
      }
    } catch (e) {
      console.warn('Aufgaben konnten nicht geladen werden', e);
      if (e instanceof ApiError && e.status === 401) setAngemeldet(false);
      else setFehler(`Laden fehlgeschlagen: ${e.message}`);
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => { neuLaden(); }, [neuLaden]);

  // Alle vorkommenden Projekte: die sieben festen zuerst, danach alles,
  // was zusaetzlich in den Daten auftaucht.
  const alleProjekte = useMemo(() => {
    const extra = new Map();
    aufgaben.forEach((t) => (t.kategorien || []).forEach((k) => {
      if (!PROJEKT_LOOKUP.has(k.toLowerCase()) && !extra.has(k.toLowerCase())) extra.set(k.toLowerCase(), k);
    }));
    return [...PROJEKTE.map((p) => p.name), ...[...extra.values()].sort((a, b) => a.localeCompare(b, 'de'))];
  }, [aufgaben]);

  const passtZumFilter = useCallback((t) => {
    // Suche zuerst: sie schlaegt die uebrigen Filter nicht, sondern
    // verengt zusaetzlich. Gesucht wird im Text, im Kundennamen, in den
    // Projekten und im Wartegrund — also in allem, was in der Zeile
    // sichtbar ist oder dahintersteckt.
    const q = suche.trim().toLowerCase();
    if (q) {
      const heuhaufen = [
        t.text || '',
        t.kunde_name || '',
        t.wartet_auf || '',
        ...(t.kategorien || []),
      ].join(' ').toLowerCase();
      if (!heuhaufen.includes(q)) return false;
    }
    if (!zeigeErledigte && t.erledigt) return false;
    if (filterPrio && (t.prioritaet || 'B') !== filterPrio) return false;
    if (filterProjekt === OHNE_PROJEKT) return (t.kategorien || []).length === 0;
    if (filterProjekt) {
      if (!gehoertZu(t, filterProjekt)) return false;
      // Unterbereich verengt zusaetzlich — hier muss es genau passen.
      if (filterUnter) {
        return (t.kategorien || []).some((k) => k.toLowerCase() === filterUnter.toLowerCase());
      }
    }
    return true;
  }, [filterProjekt, filterUnter, filterPrio, zeigeErledigte, suche]);

  // Sortierung: Gewichtung zuerst — IMMER, egal welcher Filter aktiv ist.
  // Danach die naehere Frist, dann die naehere Wiedervorlage, Leeres
  // jeweils ans Ende.
  const sortiert = useCallback((liste) => [...liste].sort((a, b) => {
    const weit = '9999-12-31';
    return (PRIO_RANG[(a.prioritaet || 'B')] ?? 1) - (PRIO_RANG[(b.prioritaet || 'B')] ?? 1)
        || (a.faellig_am || weit).localeCompare(b.faellig_am || weit)
        || (a.wiedervorlage_am || weit).localeCompare(b.wiedervorlage_am || weit)
        || (a.created_at || '').localeCompare(b.created_at || '');
  }), []);

  const gruppen = useMemo(() => {
    const sichtbar = aufgaben.filter(passtZumFilter);
    const offen = sichtbar.filter((t) => !t.erledigt);
    return {
      prio: PRIOS.map((p) => ({ ...p, eintraege: sortiert(offen.filter((t) => (t.prioritaet || 'B') === p.code)) }))
                 .filter((g) => g.eintraege.length > 0),
      erledigt: sortiert(sichtbar.filter((t) => t.erledigt)),
      anzahl: sichtbar.length,
    };
  }, [aufgaben, passtZumFilter, sortiert]);

  const aendere = async (aufgabe, patch) => {
    setSpeichert(aufgabe.id); setFehler(null); setKonflikt(null);
    try {
      const neu = await api.put(`/api/todos/${aufgabe.id}`, { ...patch, updated_at_erwartet: aufgabe.updated_at });
      setAufgaben((alt) => alt.map((t) => (t.id === neu.id ? { ...neu, kunde_name: t.kunde_name } : t)));
    } catch (e) {
      console.warn('Schreiben fehlgeschlagen', e);
      if (e instanceof ApiError && e.status === 409) {
        setKonflikt(e.detail || 'Die Aufgabe wurde zwischenzeitlich geändert.');
        await neuLaden();
      } else {
        setFehler(`Nicht gespeichert: ${e.message}`);
      }
    } finally {
      setSpeichert(null);
    }
  };

  // Antwort auf "Hat der Termin stattgefunden?". Die Zeile verschwindet
  // erst, wenn der Server bestaetigt hat.
  const termiNantworten = async (eintrag, durchgefuehrt) => {
    setNachfrageLaeuft(eintrag.termin_id);
    setFehler(null); setNachfrageEcho(null);
    try {
      const r = await api.post(`/api/termine/${eintrag.termin_id}/durchgefuehrt`, { durchgefuehrt });
      setNachfragen((alt) => alt.filter((x) => x.termin_id !== eintrag.termin_id));
      if (!durchgefuehrt) {
        setNachfrageEcho(r?.aufgabe_angelegt
          ? `„${eintrag.titel}" als nicht wahrgenommen vermerkt. Aufgabe „Ersatztermin klären" wurde angelegt.`
          : `„${eintrag.titel}" als nicht wahrgenommen vermerkt. Die Aufgabe dazu gab es schon.`);
        await neuLaden();           // neue Aufgabe soll sofort in der Liste stehen
      } else if (eintrag.protokoll_fehlt) {
        setNachfrageEcho(`„${eintrag.titel}" als durchgeführt vermerkt. Das Protokoll fehlt noch — steht jetzt unter SPA · Protokolle.`);
        await neuLaden();
      } else {
        setNachfrageEcho(`„${eintrag.titel}" als durchgeführt vermerkt.`);
      }
    } catch (e) {
      console.warn('Termin-Antwort fehlgeschlagen', e);
      setFehler(`Nicht gespeichert: ${e.message}`);
    } finally {
      setNachfrageLaeuft(null);
    }
  };

  const anlegen = async () => {
    const text = neuText.trim();
    if (!text) return;
    setFehler(null);
    try {
      await api.post('/api/todos', {
        text,
        faellig_am: neuFrist || null,
        kategorien: neuProjekt.trim()
          ? [neuProjekt.trim()]
          : (filterProjekt && filterProjekt !== OHNE_PROJEKT ? [filterProjekt] : []),
      });
      setNeuText(''); setNeuFrist(''); setNeuProjekt('');
      await neuLaden();
    } catch (e) {
      console.warn('Anlegen fehlgeschlagen', e);
      setFehler(`Nicht angelegt: ${e.message}`);
    }
  };

  const anzahlIn = (projekt) => aufgaben.filter((t) =>
    !t.erledigt && (projekt === OHNE_PROJEKT
      ? (t.kategorien || []).length === 0
      : gehoertZu(t, projekt))
  ).length;

  // Genauer Treffer, fuer die Unterbereiche.
  const anzahlGenau = (wert) => aufgaben.filter((t) =>
    !t.erledigt && (t.kategorien || []).some((k) => k.toLowerCase() === wert.toLowerCase())
  ).length;

  const projektWechseln = (k) => {
    setFilterProjekt((alt) => (alt === k ? null : k));
    setFilterUnter(null);           // Unterauswahl nie aus einem anderen Projekt mitschleppen
    setOffenesPanel(null);          // Leiste zuklappen, Liste zeigen
  };

  // Das aktuell gewaehlte Projekt, falls es Unterbereiche hat.
  const aktuellesProjekt = filterProjekt && filterProjekt !== OHNE_PROJEKT
    ? PROJEKT_LOOKUP.get(filterProjekt.toLowerCase()) : null;
  const unterbereiche = (aktuellesProjekt && aktuellesProjekt.unter) || [];

  if (!angemeldet) return <AnmeldungNoetig onFertig={neuLaden} />;

  /* ---------------------- eine Aufgaben-Zeile ------------------------- */
  const Zeile = (t) => {
    const offen = offeneId === t.id;
    const prio = PRIOS.find((p) => p.code === (t.prioritaet || 'B')) || PRIOS[1];
    // Wenn ein Unterbereich dransteht, ist das Oberprojekt-Kuerzel
    // ueberfluessig — "SPA PRO" nebeneinander sagt nicht mehr als "PRO".
    const roh = t.kategorien || [];
    const mitUnterbereich = new Set(
      roh.filter((k) => k.includes(': ')).map((k) => k.split(': ')[0].toLowerCase())
    );
    const projekte = roh.filter((k) => !mitUnterbereich.has(k.toLowerCase())).map(projektInfo);
    const frist = lageText(t.faellig_am);
    const ueberfaellig = tageBis(t.faellig_am) !== null && tageBis(t.faellig_am) < 0;

    return (
      <li key={t.id}
          className={`overflow-hidden rounded-xl border bg-white ${
            t.erledigt ? 'border-gray-200 opacity-60' : 'border-gray-300'
          } ${speichert === t.id ? 'ring-2 ring-blue-300' : ''}`}>

        {/* --- kompakte Zeile --- */}
        <div className="flex items-center gap-2 p-2">
          <button
            onClick={(e) => { e.stopPropagation(); aendere(t, { erledigt: !t.erledigt }); }}
            title={t.erledigt ? 'wieder öffnen' : 'erledigen'}
            className={`flex shrink-0 items-center justify-center rounded-full border-2 text-lg ${
              t.erledigt ? 'border-green-600 bg-green-600 text-white' : 'border-gray-400 text-transparent hover:border-green-600'
            }`}
            style={{ width: 44, height: 44 }}
          >✓</button>

          <button onClick={() => setOffeneId(offen ? null : t.id)}
                  className="flex min-w-0 flex-1 items-start gap-2 py-1 text-left"
                  style={{ minHeight: 44 }}>
            <div className="min-w-0 flex-1">
              {/* Zeile 1: Kuerzel + Text. Bekommt die volle Breite, weil
                  die Gewichtung schon in der Gruppen-Ueberschrift steht —
                  ein Abzeichen je Zeile waere doppelt und frisst auf dem
                  Handy genau den Platz, den der Text braucht. */}
              <div className="flex min-w-0 items-center gap-1.5">
                {/* Erstellerkennzeichen. Nur ein Bild, kein Text —
                    Hendriks Aufgaben bleiben ohne Symbol. Der
                    Alternativtext ist fuer Screenreader da, nicht
                    sichtbar. */}
                {t.ersteller_avatar === 'jan' && (
                  <img src="/jan-avatar.png" alt="Von Jan erstellt" title="Von Jan erstellt"
                       width="26" height="26"
                       className="h-[26px] w-[26px] shrink-0 object-contain" />
                )}
                {projekte.length === 0 && (
                  <span className="shrink-0 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-400">—</span>
                )}
                {projekte.slice(0, 2).map((pi) => (
                  <span key={pi.kuerzel} title={pi.name}
                        className={`shrink-0 rounded border px-1.5 py-0.5 text-xs font-bold ${pi.chip}`}>
                    {pi.kuerzel}
                  </span>
                ))}
                {projekte.length > 2 && (
                  <span className="shrink-0 rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-600">
                    +{projekte.length - 2}
                  </span>
                )}
                <span className={`min-w-0 flex-1 truncate text-base ${t.erledigt ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                  {t.text}
                </span>
              </div>

              {/* Zeile 2: Fristen, klein. Nur wenn es etwas zu sagen gibt. */}
              {(frist || t.wiedervorlage_am || t.wartet_auf) && (
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                  {frist && (
                    <span className={ueberfaellig ? 'font-semibold text-red-700' : 'text-gray-500'}>
                      Frist {frist}
                    </span>
                  )}
                  {t.wiedervorlage_am && (
                    <span className="text-blue-700">Wiedervorlage {lageText(t.wiedervorlage_am)}</span>
                  )}
                  {t.wartet_auf && <span className="text-amber-700">wartet auf {t.wartet_auf}</span>}
                </div>
              )}
            </div>
            <span className="shrink-0 pt-1 text-gray-400">{offen ? '▾' : '▸'}</span>
          </button>

          {t.kunde_id && (
            <a href={`/?kunde=${encodeURIComponent(t.kunde_id)}`} target="_blank" rel="noreferrer"
               onClick={(e) => e.stopPropagation()}
               title={t.kunde_name ? `Akte von ${t.kunde_name} öffnen` : 'Akte öffnen'}
               className="flex shrink-0 items-center rounded-lg border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-100"
               style={{ minHeight: 44 }}>
              Akte
            </a>
          )}
        </div>

        {/* --- Bearbeitung, erst auf Klick --- */}
        {offen && (
          <div className="border-t border-gray-200 bg-gray-50 p-3">
            <p className="mb-2 text-xs text-gray-500">
              {t.kunde_id ? (
                <a href={`/?kunde=${encodeURIComponent(t.kunde_id)}`} target="_blank" rel="noreferrer"
                   className="font-semibold text-blue-800 underline decoration-dotted underline-offset-2">
                  {t.kunde_name || 'Akte öffnen'}
                </a>
              ) : 'ohne Kundenbezug'}
              {t.erledigt && t.erledigt_am ? ' · erledigt' : ''}
            </p>

            <div className="mb-3">
              <label className="text-xs text-gray-500">Aufgabentext</label>
              <input defaultValue={t.text}
                     onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                     onBlur={(e) => {
                       const v = e.target.value.trim();
                       if (!v) { e.target.value = t.text; return; }  // leer ist nicht erlaubt
                       if (v !== t.text) aendere(t, { text: v });
                     }}
                     className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 text-base"
                     style={{ minHeight: 44 }} />
            </div>

            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">Gewichtung:</span>
              {PRIOS.map((p) => (
                <button key={p.code} onClick={() => aendere(t, { prioritaet: p.code })}
                        className={`rounded-lg border px-3 text-sm font-bold ${
                          (t.prioritaet || 'B') === p.code ? p.an : `bg-white ${p.aus}`}`}
                        style={{ minHeight: 36 }}>
                  {p.code} — {p.erklaerung}
                </button>
              ))}
            </div>

            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">Projekte:</span>
              {(t.kategorien || []).map((k) => {
                const p = projektInfo(k);
                return (
                  <span key={k} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm ${p.chip}`}>
                    <strong>{p.kuerzel}</strong> {p.name}
                    <button onClick={() => aendere(t, { kategorien: (t.kategorien || []).filter((x) => x !== k) })}
                            title={`„${k}" entfernen`} className="ml-1 opacity-60 hover:opacity-100">×</button>
                  </span>
                );
              })}
              <ProjektHinzufuegen onHinzufuegen={(neu) => {
                const vorhanden = t.kategorien || [];
                if (vorhanden.some((x) => x.toLowerCase() === neu.toLowerCase())) return;
                aendere(t, { kategorien: [...vorhanden, neu] });
              }} />
            </div>

            {/* Unterbereiche — nur fuer Projekte, die welche haben (SPA). */}
            {PROJEKTE.filter((pr) => pr.unter && gehoertZu(t, pr.name)).map((pr) => (
              <div key={pr.name} className="mb-3 flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500">{pr.name} —</span>
                {pr.unter.map((u) => {
                  const an = (t.kategorien || []).some((k) => k.toLowerCase() === u.name.toLowerCase());
                  return (
                    <button key={u.name}
                            onClick={() => {
                              const vorhanden = t.kategorien || [];
                              if (an) {
                                aendere(t, { kategorien: vorhanden.filter((k) => k.toLowerCase() !== u.name.toLowerCase()) });
                              } else {
                                // Oberprojekt sicherstellen, damit die Aufgabe
                                // auch bei "SPA alles" auftaucht.
                                const ohneDoppel = vorhanden.filter((k) => k.toLowerCase() !== pr.name.toLowerCase());
                                aendere(t, { kategorien: [pr.name, ...ohneDoppel, u.name] });
                              }
                            }}
                            className={`rounded-full border px-3 text-sm ${an ? `${pr.chip} ring-2 ring-gray-900 ring-offset-1` : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-100'}`}
                            style={{ minHeight: 36 }}>
                      <strong>{u.kuerzel}</strong> {u.label}
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="flex flex-wrap gap-3">
              <DatumsFeld titel="Frist" farbe="text-gray-900" hinweis="verbindlich zugesagt"
                          wert={t.faellig_am} onAendern={(v) => aendere(t, { faellig_am: v })} />
              <DatumsFeld titel="Wiedervorlage" farbe="text-blue-800" hinweis="nur meine Erinnerung"
                          wert={t.wiedervorlage_am} onAendern={(v) => aendere(t, { wiedervorlage_am: v })} />
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">Wiedervorlage:</span>
              <button onClick={() => aendere(t, { wiedervorlage_am: plusTage(t.wiedervorlage_am || heuteISO(), 1) })}
                      className="rounded-lg border border-blue-300 bg-blue-50 px-3 text-sm text-blue-900"
                      style={{ minHeight: 44 }}>+ 1 Tag</button>
              <button onClick={() => aendere(t, { wiedervorlage_am: plusTage(t.wiedervorlage_am || heuteISO(), 7) })}
                      className="rounded-lg border border-blue-300 bg-blue-50 px-3 text-sm text-blue-900"
                      style={{ minHeight: 44 }}>+ 1 Woche</button>
              {t.wiedervorlage_am && (
                <button onClick={() => aendere(t, { wiedervorlage_am: null })}
                        className="rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700"
                        style={{ minHeight: 44 }}>keine</button>
              )}
              <span className="ml-auto text-xs text-gray-400">
                {t.wiedervorlage_am
                  ? `Erinnerung ${deutsch(t.wiedervorlage_am)} — ändert die Frist nicht`
                  : 'keine Erinnerung — es kommt keine Nachfrage'}
              </span>
            </div>

            <div className="mt-2">
              <label className="text-xs text-gray-500">Wartet auf (leer = wartet nicht)</label>
              <input defaultValue={t.wartet_auf || ''}
                     onBlur={(e) => {
                       const v = e.target.value.trim();
                       if (v !== (t.wartet_auf || '')) aendere(t, { wartet_auf: v || null });
                     }}
                     placeholder="z. B. Rückmeldung der Pflegekasse"
                     className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 text-base"
                     style={{ minHeight: 44 }} />
            </div>
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 px-3 py-4 sm:px-6">
      <datalist id="pd-projekte">
        {alleProjekte.map((k) => <option key={k} value={k} />)}
      </datalist>

      <div className="mx-auto max-w-3xl">
        <header className="mb-4">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Gemeinsame Aufgaben</h1>
              <p className="mt-1 text-sm text-gray-600">
                Dieselben Aufgaben wie im Cockpit — keine Kopie.
              </p>
            </div>

            {/* Auf dem Telefon nur die Lupe — sie klappt das Feld darunter
                auf. Ab kleiner Breite steht das Feld gleich da. */}
            <button onClick={() => { setSucheOffen((v) => !v); if (sucheOffen) setSuche(''); }}
                    title="Suchen"
                    className={`flex shrink-0 items-center justify-center rounded-lg border sm:hidden ${
                      sucheOffen || suche ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-700'}`}
                    style={{ width: 44, height: 44 }}>
              <Search className="h-5 w-5" />
              <span className="sr-only">Suchen</span>
            </button>

            <div className="relative hidden sm:block" style={{ width: 260 }}>
              <input value={suche} onChange={(e) => setSuche(e.target.value)}
                     placeholder="Suchen…"
                     className="w-full rounded-lg border border-gray-300 pl-9 pr-8 text-base"
                     style={{ minHeight: 44 }} />
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              {suche && (
                <button onClick={() => setSuche('')} title="Suche leeren"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-900">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* ausgeklapptes Suchfeld auf schmalen Schirmen */}
          {sucheOffen && (
            <div className="relative mt-2 sm:hidden">
              <input value={suche} onChange={(e) => setSuche(e.target.value)}
                     autoFocus placeholder="Suchen in Aufgaben, Kunden, Projekten…"
                     className="w-full rounded-lg border border-gray-300 pl-9 pr-8 text-base"
                     style={{ minHeight: 44 }} />
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              {suche && (
                <button onClick={() => setSuche('')} title="Suche leeren"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </header>

        {konflikt && (
          <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <strong>Nicht gespeichert — Konflikt.</strong> {konflikt}
            <button onClick={() => setKonflikt(null)} className="ml-2 underline">verstanden</button>
          </div>
        )}
        {fehler && (
          <div className="mb-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            {fehler}<button onClick={neuLaden} className="ml-2 underline">neu laden</button>
          </div>
        )}

        {/* ------------------------------------------------------------
            Ganz oben, weil es die Frage ist, die sonst untergeht: hat
            der Termin stattgefunden? Erscheint nur, wenn es wirklich
            offene gibt — und am Termintag erst am spaeten Nachmittag.
           ------------------------------------------------------------ */}
        {nachfragen.length > 0 && (
          <div className="mb-3 rounded-xl border-2 border-amber-400 bg-amber-50 p-3">
            <h2 className="text-base font-bold text-amber-900">
              {nachfragen.length === 1
                ? 'Hat dieser Termin stattgefunden?'
                : `Haben diese ${nachfragen.length} Termine stattgefunden?`}
            </h2>
            <p className="mb-2 mt-0.5 text-xs text-amber-800">
              Später lässt sich das kaum noch rekonstruieren — deshalb jetzt
              kurz beantworten.
            </p>
            <ul className="space-y-2">
              {nachfragen.map((n) => (
                <li key={n.termin_id}
                    className={`flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-white p-2 ${
                      nachfrageLaeuft === n.termin_id ? 'opacity-50' : ''}`}>
                  <div className="min-w-0 flex-1">
                    {/* Name als Link: um zu beurteilen, ob der Termin
                        stattgefunden hat, muss man oft in die Akte sehen. */}
                    {n.kunde_id ? (
                      <a href={`/?kunde=${encodeURIComponent(n.kunde_id)}`} target="_blank" rel="noreferrer"
                         title={`Akte von ${n.titel} öffnen`}
                         className="block truncate text-base font-semibold text-blue-800 underline decoration-dotted underline-offset-2">
                        {n.titel}
                      </a>
                    ) : (
                      <p className="truncate text-base text-gray-900">{n.titel}</p>
                    )}
                    <p className="text-xs text-gray-500">
                      {deutsch(n.datum)}{n.uhrzeit ? ` · ${n.uhrzeit}` : ''} · {n.art}
                    </p>
                  </div>
                  <button onClick={() => termiNantworten(n, true)}
                          disabled={nachfrageLaeuft === n.termin_id}
                          className="rounded-lg border border-green-600 bg-green-600 px-4 text-sm font-semibold text-white disabled:opacity-50"
                          style={{ minHeight: 44 }}>Ja</button>
                  <button onClick={() => termiNantworten(n, false)}
                          disabled={nachfrageLaeuft === n.termin_id}
                          className="rounded-lg border border-gray-400 bg-white px-4 text-sm font-semibold text-gray-700 disabled:opacity-50"
                          style={{ minHeight: 44 }}>Nein</button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {nachfrageEcho && (
          <div className="mb-3 rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-900">
            {nachfrageEcho}
            <button onClick={() => setNachfrageEcho(null)} className="ml-2 underline">ok</button>
          </div>
        )}

        {/* ------------------------------------------------------------
            Schmale Leiste statt dreier aufgeklappter Kaesten. Alles ist
            zu, bis man es braucht — damit auf dem Handy sofort die Liste
            kommt und nicht erst eine Bildschirmhoehe Auswahl.
           ------------------------------------------------------------ */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button onClick={() => setOffenesPanel(offenesPanel === 'neu' ? null : 'neu')}
                  className={`rounded-lg border px-4 text-sm font-semibold ${
                    offenesPanel === 'neu'
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-900 bg-white text-gray-900 hover:bg-gray-100'}`}
                  style={{ minHeight: 44 }}>
            + To-Dos
          </button>

          <button onClick={() => setOffenesPanel(offenesPanel === 'projekt' ? null : 'projekt')}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 text-sm ${
                    offenesPanel === 'projekt' ? 'border-gray-900 bg-gray-100' : 'border-gray-300 bg-white'} text-gray-800 hover:bg-gray-100`}
                  style={{ minHeight: 44 }}>
            <span className="text-gray-500">Projekt:</span>
            {filterProjekt
              ? (filterProjekt === OHNE_PROJEKT
                  ? <strong>ohne Projekt</strong>
                  : <span className={`rounded border px-1.5 py-0.5 text-xs font-bold ${projektInfo(filterProjekt).chip}`}>
                      {projektInfo(filterProjekt).kuerzel}
                    </span>)
              : <strong>alle</strong>}
            <span className="text-gray-400">{offenesPanel === 'projekt' ? '▾' : '▸'}</span>
          </button>

          <button onClick={() => setOffenesPanel(offenesPanel === 'prio' ? null : 'prio')}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 text-sm ${
                    offenesPanel === 'prio' ? 'border-gray-900 bg-gray-100' : 'border-gray-300 bg-white'} text-gray-800 hover:bg-gray-100`}
                  style={{ minHeight: 44 }}>
            <span className="text-gray-500">Prio:</span>
            <strong>{filterPrio || 'alle'}</strong>
            <span className="text-gray-400">{offenesPanel === 'prio' ? '▾' : '▸'}</span>
          </button>

          {(filterProjekt || filterPrio || zeigeErledigte) && (
            <button onClick={() => { setFilterProjekt(null); setFilterUnter(null); setFilterPrio(null); setZeigeErledigte(false); }}
                    className="rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-600 hover:bg-gray-100"
                    style={{ minHeight: 44 }}>
              Filter zurücksetzen
            </button>
          )}
        </div>

        {/* --- Klappe: neue Aufgabe --- */}
        {offenesPanel === 'neu' && (
          <div className="mb-3 rounded-xl border border-gray-200 bg-white p-3">
            <div className="flex flex-col gap-2 sm:flex-row">
              <input value={neuText} onChange={(e) => setNeuText(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') anlegen(); }}
                     autoFocus placeholder="Was ist zu tun?"
                     className="flex-1 rounded-lg border border-gray-300 px-3 text-base" style={{ minHeight: 44 }} />
              <input list="pd-projekte" value={neuProjekt} onChange={(e) => setNeuProjekt(e.target.value)}
                     placeholder="Projekt"
                     className="w-full rounded-lg border border-gray-300 px-3 text-base sm:w-44" style={{ minHeight: 44 }} />
              <input type="date" value={neuFrist} onChange={(e) => setNeuFrist(e.target.value)} title="Frist (optional)"
                     className="rounded-lg border border-gray-300 px-2 text-base" style={{ minHeight: 44 }} />
              <button onClick={anlegen} disabled={!neuText.trim()}
                      className="rounded-lg bg-gray-900 px-4 text-base font-semibold text-white disabled:opacity-40"
                      style={{ minHeight: 44 }}>Hinzufügen</button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Ist oben ein Projekt gewählt, wird es automatisch übernommen.
            </p>
          </div>
        )}

        {/* --- Klappe: Projektauswahl --- */}
        {offenesPanel === 'projekt' && (
          <div className="mb-3 rounded-xl border border-gray-200 bg-white p-3">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => projektWechseln(null)}
                      className={`rounded-full border px-3 text-sm ${
                        !filterProjekt ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'}`}
                      style={{ minHeight: 40 }}>Alle</button>
              {alleProjekte.map((k) => {
                const pi = projektInfo(k);
                const n = anzahlIn(k);
                return (
                  <button key={k} onClick={() => projektWechseln(k)}
                          className={`inline-flex items-center gap-2 rounded-full border px-3 text-sm ${pi.chip} ${
                            filterProjekt === k ? 'ring-2 ring-gray-900 ring-offset-1' : ''} ${n === 0 ? 'opacity-50' : ''}`}
                          style={{ minHeight: 40 }}>
                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${pi.balken}`} />
                    <strong>{pi.kuerzel}</strong><span>{pi.name}</span>
                    <span className="opacity-60">{n}</span>
                  </button>
                );
              })}
              <button onClick={() => projektWechseln(OHNE_PROJEKT)}
                      className={`rounded-full border px-3 text-sm ${
                        filterProjekt === OHNE_PROJEKT ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'}`}
                      style={{ minHeight: 40 }}>
                ohne Projekt <span className="opacity-60">{anzahlIn(OHNE_PROJEKT)}</span>
              </button>
            </div>
          </div>
        )}

        {/* --- Klappe: Gewichtung --- */}
        {offenesPanel === 'prio' && (
          <div className="mb-3 rounded-xl border border-gray-200 bg-white p-3">
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => { setFilterPrio(null); setOffenesPanel(null); }}
                      className={`rounded-full border px-3 text-sm ${
                        !filterPrio ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'}`}
                      style={{ minHeight: 40 }}>Alle</button>
              {PRIOS.map((pp) => (
                <button key={pp.code}
                        onClick={() => { setFilterPrio(filterPrio === pp.code ? null : pp.code); setOffenesPanel(null); }}
                        className={`rounded-full border px-3 text-sm ${
                          filterPrio === pp.code ? pp.an : `bg-white ${pp.aus}`}`}
                        style={{ minHeight: 40 }}>
                  {pp.code} — {pp.erklaerung}
                </button>
              ))}
              <label className="ml-auto flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" checked={zeigeErledigte}
                       onChange={(e) => setZeigeErledigte(e.target.checked)} className="h-5 w-5" />
                Erledigte zeigen
              </label>
            </div>
          </div>
        )}

        {/* --- Unterbereiche: nur bei einem Projekt, das welche hat --- */}
        {unterbereiche.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-2">
            <span className="pl-1 text-xs uppercase tracking-wide text-gray-500">
              {projektInfo(filterProjekt).name}:
            </span>
            <button onClick={() => setFilterUnter(null)}
                    className={`rounded-full border px-3 text-sm ${
                      !filterUnter ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'}`}
                    style={{ minHeight: 40 }}>
              alles <span className="opacity-60">{anzahlIn(filterProjekt)}</span>
            </button>
            {unterbereiche.map((u) => {
              const ui = projektInfo(u.name);
              return (
                <button key={u.name}
                        onClick={() => setFilterUnter(filterUnter === u.name ? null : u.name)}
                        className={`inline-flex items-center gap-2 rounded-full border px-3 text-sm ${ui.chip} ${
                          filterUnter === u.name ? 'ring-2 ring-gray-900 ring-offset-1' : ''}`}
                        style={{ minHeight: 40 }}>
                  <strong>{u.kuerzel}</strong><span>{u.label}</span>
                  <span className="opacity-60">
                    {anzahlGenau(u.name) + ((hinweise[u.quelle] || []).length)}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {laden && <p className="text-sm text-gray-500">lädt…</p>}
        {!laden && gruppen.anzahl === 0 && (
          <p className="text-sm text-gray-500">
            {aufgaben.length === 0
              ? 'Keine Aufgaben vorhanden.'
              : (suche.trim()
                  ? `Nichts gefunden zu „${suche.trim()}".`
                  : 'Keine Aufgabe passt zu diesem Filter.')}
          </p>
        )}

        {/* ------------------------------------------------------------
            Was das Cockpit selbst weiss. Keine ToDos, deshalb kein Haken
            und keine Gewichtung: die Zeilen verschwinden von allein,
            sobald das Protokoll existiert, die Anfrage beantwortet oder
            die Tour bestaetigt ist.
           ------------------------------------------------------------ */}
        {unterbereiche
          .filter((u) => (!filterUnter || filterUnter === u.name) && (hinweise[u.quelle] || []).length > 0)
          .map((u) => {
            const ui = projektInfo(u.name);
            const eintraege = hinweise[u.quelle] || [];
            return (
              <section key={u.name} className="mb-5">
                <h2 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-700">
                  <span className={`rounded border px-1.5 py-0.5 text-xs ${ui.chip}`}>{u.kuerzel}</span>
                  {u.label} aus dem Cockpit
                  <span className="font-normal opacity-60">({eintraege.length})</span>
                </h2>
                <ul className="space-y-2">
                  {eintraege.map((e) => (
                    <li key={e.id}
                        className={`flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 border-l-4 ${ui.balken.replace('bg-', 'border-l-')}`}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-base text-gray-900">{e.titel}</p>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {e.datum ? deutsch(e.datum) : 'ohne Datum'}{e.zusatz ? ` · ${e.zusatz}` : ''}
                        </p>
                      </div>
                      {e.kunde_id && (
                        <a href={`/?kunde=${encodeURIComponent(e.kunde_id)}`} target="_blank" rel="noreferrer"
                           className="shrink-0 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          Akte
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-gray-400">
                  Kommt direkt aus dem Cockpit — nichts zum Abhaken. Die Zeile
                  verschwindet, sobald die Sache dort erledigt ist.
                </p>
              </section>
            );
          })}

        {/* --- Liste, immer nach Gewichtung gruppiert --- */}
        {gruppen.prio.map((g) => (
          <section key={g.code} className="mb-5">
            <h2 className={`mb-2 text-sm font-bold uppercase tracking-wide ${g.kopf}`}>
              {g.code} — {g.erklaerung} <span className="font-normal opacity-60">({g.eintraege.length})</span>
            </h2>
            <ul className="space-y-2">{g.eintraege.map(Zeile)}</ul>
          </section>
        ))}

        {zeigeErledigte && gruppen.erledigt.length > 0 && (
          <section className="mb-5">
            <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">
              Erledigt <span className="font-normal opacity-60">({gruppen.erledigt.length})</span>
            </h2>
            <ul className="space-y-2">{gruppen.erledigt.map(Zeile)}</ul>
          </section>
        )}

        <p className="mt-6 text-xs text-gray-400">
          Eine Erinnerung, verstrichene Zeit oder eine fehlende Antwort setzt
          eine Aufgabe nie auf „erledigt". Das passiert ausschließlich über den
          Haken links.
        </p>
      </div>
    </div>
  );
}
